import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { SidebarTasksComponent } from '@app/modules/calendar/components/sidebar-tasks/sidebar-tasks.component';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { Filter } from '@app/shared/models/filter';
import { ManualBooking } from '@app/shared/services/manual-bookings/manual-bookings.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { addMonths, eachDay, endOfMonth, format, isSameDay, subDays } from 'date-fns';
import { BehaviorSubject, combineLatest, fromEvent, lastValueFrom, zip } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { PropertyCalendarReferrerService } from '../../services/property-calendar-referrer.service';

import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { SidebarPricingSaveEvent } from '@app/modules/calendar/components/sidebar-pricing/sidebar-pricing.component';
import { CELL_HEIGHT, CELL_WIDTH } from '@app/modules/calendar/constants';
import { FetchPropertiesApiResponse } from '@app/modules/settings/services/active-properties/active-properties.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { DirectCustomQuote } from '@app/shared/services/direct-quotes/direct-quotes.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { CalendarStateService, Property } from '../../services/calendar-state.service';

const PROPERTY_LIMIT = 25;
const PAST_DATES_COUNT = 7;
const HIDER_HEIGHT = 18;
const DESKTOP_SCROLLBAR_HEIGHT = 15; // osx is 16, windows is 17 but 15 works best to offset the scrollbar from the content
// the calendar should not show the Muted filter since the calendar does not allow muted properties
const FILTER_URL = '/filters/properties?for_calendar=true';

/**
 * This component does not use OnPush change detection strategy,
 * but the most expensive part of the dom is in `sbnb-calendar-cell`, which does.
 * In order to trigger change detection in `sbnb-calendar-cell`,
 * we push changes to the `properties$` BehaviorSubject in the `CalendarService`.
 * This is hacky and not sustainable - if you need to add something,
 * please consider refactoring what you need to use and using observables
 * that are subscribed to in the template.
 */
@Component({
  standalone: false,
  selector: 'sbnb-multi-calendar',
  templateUrl: './multi-calendar.component.html',
  styleUrls: ['./multi-calendar.component.scss'],
  providers: [CalendarStateService],
})
export class MultiCalendarComponent extends OnDestroyComponent implements OnInit {
  public filterUrl = FILTER_URL;
  Math = Math;

  FAILURE_TYPES = {
    price: 'price',
    available: 'availability',
    minimum_length_of_stay: 'min_stay',
  };

  public loadingMoreItems = false;
  public loading = true;
  public saving = false;

  readonly rowWidth: number = CELL_WIDTH;
  readonly rowHeight: number = CELL_HEIGHT;
  monthsToLoad = 3;

  startDate: Date = new Date();

  dates: Date[] = [];
  properties: Property[] = [];

  clickedDate: Date;
  clickedProperty: Property;
  hoveredDates: Date[] = [];

  affectedProperties: Property[]; // Multiple properties to be updated

  desiredPrice: number;
  desiredAvailability: boolean = null;
  desiredLengthOfStay: number;
  desiredMaxLengthOfStay: number;
  desiredNote: string;
  daysOfTheWeek: number[];
  desiredClosedForCheckin?: boolean;
  desiredClosedForChekout?: boolean;

  selectedReservation: any;

  searchCriteria$ = new BehaviorSubject<string>('');
  activeFilters: any[] = [];

  selectedTask: any;

  daysTasks: any[];

  minimisedLeft = false; // Collapsible left panel, mostly for space on mobile

  hasMoreProperties = false; // Set when fetching properties to let us know if there are more to infinitely load
  currentPropertyOffset = 0;

  selectedPropertyIds: Record<string, boolean> = {};
  selectedDates: Record<string, boolean> = {};
  optimisticUpdates = this.calendarStateService.optimisticUpdates;

  @ViewChild('sidebarTasks', { static: false })
  sidebarTasks: SidebarTasksComponent;

  @ViewChild('scroller', { static: true })
  scroller: ElementRef;

  @ViewChild('filterComponent', { static: false })
  filterComponent: FilterComponent;

  @ViewChild('dateMenuTrigger') dateMenuTrigger: MatMenuTrigger;
  @ViewChild('drawer', { static: true }) drawer: MatDrawer;
  @ViewChild('rightHeaders', { static: false }) rightHeaders: ElementRef;
  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
  @Input() data: FetchPropertiesApiResponse;

  Features = Features;
  public calendarPermissions$ = this.calendarStateService.calendarPermissions$;
  public mode$ = this.calendarStateService.mode$;
  public setMode = this.calendarStateService.setMode;
  private isMobile$ = this.deviceDetectionService.isMobileViewport();

  public calendarHeight$ = combineLatest([
    this.calendarStateService.properties$,
    this.isMobile$,
    this.searchCriteria$,
  ]).pipe(
    map(([properties, isMobile, searchCriteria]) => {
      const length =
        searchCriteria.length === 0
          ? properties.length
          : properties.filter((property) => property.name.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1)
              .length;
      return CELL_HEIGHT * length + CELL_HEIGHT + HIDER_HEIGHT + (isMobile ? 0 : DESKTOP_SCROLLBAR_HEIGHT);
    })
  );

  constructor(
    private readonly propertiesService: PropertiesService,
    private readonly reservationService: ReservationService,
    private readonly router: Router,
    private readonly propertyCalendarReferrerService: PropertyCalendarReferrerService,
    private readonly taskService: TasksService,
    private readonly segmentIoService: SegmentIoService,
    private titleService: TitleService,
    private calendarStateService: CalendarStateService,
    private deviceDetectionService: DeviceDetectionService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initialLoad(this.data);
  }

  initialLoad(data?: FetchPropertiesApiResponse) {
    this.loading = true;

    this.titleService.manuallySet('Calendar');

    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      this.monthsToLoad = isMobile ? 1 : 5;

      this.dates = this.fillDates(
        subDays(this.startDate, PAST_DATES_COUNT),
        endOfMonth(addMonths(this.startDate, this.monthsToLoad))
      );

      this.fetchProperties(null, data);
      this.loadMorePropertiesOnScroll();
    });

    setTimeout(() => {
      this.scrollToSelectedDate();
    }, 2000);
  }

  private scrollToSelectedDate() {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile && this.viewPort?.elementRef?.nativeElement) {
        this.viewPort.elementRef.nativeElement.scrollLeft = (PAST_DATES_COUNT - 1) * CELL_WIDTH;
      }
    });
  }

  updateProperties() {
    this.calendarStateService.properties$.next([...this.properties]);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    this.clearSelected();
  }

  loadMorePropertiesOnScroll() {
    fromEvent(this.scroller.nativeElement, 'scroll')
      .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((res: UIEvent) => {
        const target: HTMLElement = res.target as HTMLElement;

        // If we've hit the bottom of the scroll, check and load in more properties
        if (target.clientHeight + target.scrollTop + 3 >= target.scrollHeight && this.hasMoreProperties) {
          this.loadingMoreItems = true;
          this.fetchProperties(this.currentPropertyOffset);
        }
      });
  }

  async fetchProperties(offset = 0, data?: FetchPropertiesApiResponse) {
    if (offset > 0) {
      this.loadingMoreItems = true;
    }

    const res =
      data ??
      (await lastValueFrom(
        this.propertiesService.getProperties({
          filterCriteria: this.activeFilters,
          offset,
          limit: PROPERTY_LIMIT,
          paginate: true,
          includes: ['children', 'calendar_restricted'],
        })
      ));

    this.loading = false;

    if (res && res.data) {
      this.hasMoreProperties =
        res.meta.pagination.page_numbers.this_page < res.meta.pagination.page_numbers.total_pages;

      this.currentPropertyOffset = res.meta.pagination.page_numbers.this_page * PROPERTY_LIMIT;

      // Setup the default events and tasks array here.
      const updatedProperties = res.data.map((property) => {
        property.events = [];
        property.tasks = [];
        return property;
      });

      // Insert child properties directly after their parent properties
      const updatedPropertiesWithChildren = [];
      updatedProperties.forEach((property) => {
        updatedPropertiesWithChildren.push(property);

        if (property.children.length > 0) {
          property.children.forEach((child) => {
            child.events = [];
            child.tasks = [];

            child.parent_id = property.id;
            child.isChild = true;
            child.lastChild = property.children[property.children.length - 1].id === child.id;

            updatedPropertiesWithChildren.push(child);
          });
        }
      });

      // Update the properties array based on the offset
      if (offset) {
        this.properties = [...this.properties, ...updatedPropertiesWithChildren];
        this.loadingMoreItems = false;
      } else {
        this.properties = updatedPropertiesWithChildren;
      }

      // De-duplicate properties based on id
      const uniqueIds = [];
      this.properties = this.properties.filter((property) => {
        if (!uniqueIds.includes(property.id)) {
          uniqueIds.push(property.id);
          return true;
        }
        return false;
      });

      this.updateProperties();

      // Fetch reservations, tasks, pricing and only for properties which we just fetched
      const updatedPropertyIds = updatedPropertiesWithChildren.map((property) => property.id);
      this.fetchReservations(updatedPropertyIds);
      this.fetchTasks(updatedPropertyIds);
      this.fetchPricingAndAvailability(updatedPropertyIds);

      // on large screens, we may not actually have enough properties to fill the screen, meaning no scroll event can occur.
      // If we don't have a scroll, and there are more properties to load in, load them in
      setTimeout(() => {
        if (!this.scrollbarVisible(this.scroller.nativeElement) && this.hasMoreProperties) {
          this.loadingMoreItems = true;
          this.fetchProperties(this.currentPropertyOffset);
        }
      }, 1000);
    }
  }

  fetchTasks(properties: any[]) {
    const propertyIds = properties;

    this.taskService
      .getTasksByDate(
        subDays(this.startDate, 7),
        endOfMonth(addMonths(this.startDate, this.monthsToLoad)),
        propertyIds,
        false,
        false
      )
      .subscribe((res) => {
        res.forEach((task) => {
          // find the property
          const propertyIndex = this.properties.findIndex((prop) => prop.id === task.property.id);

          // if the property doesn't exist, exit out
          if (propertyIndex === -1) {
            console.log(`[Calendar] Tasks: Could not find Property: ${task.property.id} for task: ${task.id}.`);
            return;
          }

          // Safety check to never insert a task which already has a matching ID
          if (this.properties[propertyIndex].tasks.findIndex((pt) => pt.uuid === task.uuid) === -1) {
            this.properties[propertyIndex].tasks.push(task);
          } else {
            console.log(
              `[Calendar] Tasks: Not inserting duplicate task. Property: ${task.property.id} for task: ${task.id}.`
            );
          }
        });
        this.updateProperties();
      });
  }

  private scrollbarVisible(element) {
    return element.scrollHeight > element.clientHeight;
  }

  private fetchPricingAndAvailability(propertyIds?: Property['id'][]) {
    if (!propertyIds) return;

    this.properties.forEach((property) => {
      if (!propertyIds.includes(property.id)) return;

      property.pricingAndAvailabilityLoading = true;

      this.propertiesService
        .getPricingAndAvailability(
          property.id,
          subDays(this.startDate, 7),
          endOfMonth(addMonths(this.startDate, this.monthsToLoad))
        )
        .pipe(
          map((res) => ({
            pricingAndAvailabilityLoading: false,
            ...res,
          }))
        )
        .subscribe((pricingAndAvailability) => {
          property = Object.assign(property, pricingAndAvailability);
          this.updateProperties();
        });
    });
  }

  fetchReservations(propertyIDs?: number[], callbackFn?: (reservations: any) => void) {
    if (!propertyIDs) {
      propertyIDs = [];

      this.properties.forEach((property) => {
        if (!propertyIDs.includes(+property.id)) {
          propertyIDs.push(+property.id);
        }
      });
    }

    this.reservationService
      .getReservationsByDate(
        subDays(this.startDate, 7),
        endOfMonth(addMonths(this.startDate, this.monthsToLoad)),
        propertyIDs,
        false,
        true
      )
      .subscribe((res) => {
        const events = res;

        // Clean slate for all properties
        propertyIDs.forEach((id) => {
          const property = this.properties.find((prop) => +prop.id === id);

          property.events = [];
        });

        events.forEach((event) => {
          // In some cases we will have a list of related properties
          // which contains all the properties that the reservation
          // could belong to, we can prioritise this. (Booking.com reservation can belong to multiple properties)
          if (event.related_properties?.length) {
            event.related_properties.forEach((property) => {
              this.addEventToProperty(property.property_id, event);
            });
          }

          // Parent / child reservations - we now need to add some reservations to multiple properties, based on the `parent_child_properties` attribute on the property (if it exists)
          if (event.parent_child_properties?.length > 0) {
            event.parent_child_properties.forEach((element) => {
              const newEvent = {
                ...event,
                isParentChildReservation: event.listing.property_id !== element.property_id,
              };

              this.addEventToProperty(element.property_id, newEvent);
            });
          }

          if (
            event.listing.property_id &&
            !event.related_properties?.length &&
            !event.parent_child_properties?.length
          ) {
            // If not related properties we can fall back to the primary
            // listing data which should contain a property id.
            this.addEventToProperty(event.listing.property_id, event);
          }
        });

        this.updateProperties();

        callbackFn ? callbackFn(res) : null;
      });
  }

  addEventToProperty(propertyId, event) {
    // Be this a property id we care for?
    const property = this.properties.find((prop) => prop.id === propertyId);

    // Yes, it be.
    if (property) {
      property.events.push(event);
    }
  }

  fillDates(startDate: any, endDate: any): any[] {
    return eachDay(startDate, endDate);
  }

  dateClicked({ date, property }: { date: Date; property: Property }) {
    zip(this.calendarStateService.mode$, this.calendarPermissions$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([currentMode, permissions]) => {
        if (currentMode !== 'tasks' && !permissions.hasCalendarAccess) {
          return;
        }

        // If we're in task mode
        if (currentMode === 'tasks') {
          if (!property.tasks) {
            this.daysTasks = [];
          } else {
            this.daysTasks = property.tasks.filter((task) => isSameDay(task.start_date, date));
          }

          this.clickedProperty = property;
          this.clickedDate = date;

          this.drawer.open();
          return;
        }

        // If we clicked on another property, build up an array of properties to update
        const tempPropertyIds = {};
        tempPropertyIds[this.clickedProperty?.id] = true;
        tempPropertyIds[property.id] = true;
        const affectedProperties = [];
        if (this.clickedProperty?.id && this.clickedProperty.id !== property.id) {
          const indexOfClicked = this.properties.findIndex((prop) => prop.id === this.clickedProperty.id);
          const indexOfEvent = this.properties.findIndex((prop) => prop.id === property.id);

          const lowerIndex = indexOfClicked < indexOfEvent ? indexOfClicked : indexOfEvent;
          const upperIndex = indexOfClicked < indexOfEvent ? indexOfEvent : indexOfClicked;

          this.properties.forEach((prop, index) => {
            if (index >= lowerIndex && index <= upperIndex) {
              affectedProperties.push(prop);
              tempPropertyIds[prop.id] = true;
            }
          });
        }

        if (this.hoveredDates.length > 0 || this.clickedDate === date) {
          if (affectedProperties.length > 0) {
            this.affectedProperties = affectedProperties;
            this.selectedPropertyIds = { ...tempPropertyIds };
          }

          this.drawer.open();
          return;
        }

        this.clickedDate = date;
        this.hoveredDates = [date];
        this.selectedDates = { [format(date, 'YYYY-MM-DD')]: true };
        this.clickedProperty = property;
        this.selectedPropertyIds = { ...tempPropertyIds };
      });
  }

  dateHovered({ date, property }: { date: Date; property: Property }) {
    if (!this.clickedDate) {
      return;
    }

    // If we clicked on another property, build up an array of properties to update
    const affectedProperties = [];
    const tempPropertyIds = {};

    if (this.clickedProperty?.id) {
      tempPropertyIds[this.clickedProperty?.id] = true;
    }

    if (this.clickedProperty?.id && this.clickedProperty.id !== property.id) {
      const indexOfClicked = this.properties.findIndex((prop) => prop.id === this.clickedProperty.id);
      const indexOfEvent = this.properties.findIndex((prop) => prop.id === property.id);

      const lowerIndex = indexOfClicked < indexOfEvent ? indexOfClicked : indexOfEvent;
      const upperIndex = indexOfClicked < indexOfEvent ? indexOfEvent : indexOfClicked;

      this.properties.forEach((prop, index) => {
        if (index >= lowerIndex && index <= upperIndex) {
          affectedProperties.push(prop);
        }
      });

      this.affectedProperties = affectedProperties;
      this.affectedProperties.forEach((property) => {
        tempPropertyIds[property.id] = true;
      });
    } else {
      this.affectedProperties = [];
    }

    if (date > this.clickedDate) {
      this.hoveredDates = eachDay(this.clickedDate, date);
    } else {
      this.hoveredDates = eachDay(date, this.clickedDate);
    }

    const temp = {};
    this.hoveredDates.forEach((date) => {
      temp[format(date, 'YYYY-MM-DD')] = true;
    });
    this.selectedDates = { ...temp };
    this.selectedPropertyIds = { ...tempPropertyIds };
  }

  miniCalDatesChanged(event: any) {
    this.properties = [];
    this.dateMenuTrigger.closeMenu();

    this.startDate = event;
    this.scroller.nativeElement.scrollLeft = 0;
    this.initialLoad();
  }

  clearSelected() {
    this.selectedReservation = null;
    this.clickedDate = null;
    this.selectedTask = null;
    if (this.sidebarTasks) {
      this.sidebarTasks.creatingTask = false;
    }
    this.hoveredDates = [];
    this.selectedDates = {};
    this.clickedProperty = null;
    this.affectedProperties = [];
    this.selectedPropertyIds = {};
  }

  filterReset() {
    this.activeFilters = [];
    this.properties = [];
    this.loading = true;
    this.filterComponent.selfReset();
    this.initialLoad();
  }

  public handleFilterChanged(filters: Filter[]) {
    this.properties = [];
    this.loading = true;
    this.activeFilters = filters;

    // Retrieve reservations based on the new filters
    this.currentPropertyOffset = null;
    this.fetchProperties();
  }

  openConversation() {
    this.router.navigate(['/inbox/thread', this.selectedReservation.thread_uuid]);
  }

  updateTask(newTask) {
    const taskToUpdate = this.selectedReservation.tasks.findIndex((x) => x.uuid === newTask.uuid);

    this.selectedReservation.tasks[taskToUpdate] = newTask;
  }

  deleteTask(uuid: string) {
    const taskToUpdate = this.selectedReservation.tasks.findIndex((x) => x.uuid === uuid);

    this.selectedReservation.tasks.splice(taskToUpdate, 1);
  }

  taskCreated(newTask) {
    this.selectedReservation.tasks.push(newTask);
  }

  reservationSelected({ reservation, property }: { reservation: any; property: any }) {
    this.selectedReservation = reservation;
    this.clickedProperty = property;

    this.drawer.open();
  }

  setPriceAndAvailabilityValues(values: SidebarPricingSaveEvent) {
    this.hoveredDates = values.dates;
    this.desiredPrice = values.price;
    this.desiredAvailability = values.availability;
    this.desiredLengthOfStay = values.los;
    this.desiredMaxLengthOfStay = values.maxStay;
    this.desiredNote = values.note;
    this.daysOfTheWeek = values.daysOfTheWeek;
    this.desiredClosedForCheckin = values.closedForCheckin;
    this.desiredClosedForChekout = values.closedForCheckout;

    this.checkSavePriceAndAvailability();
  }

  checkSavePriceAndAvailability() {
    if (this.affectedProperties?.length > 0) {
      this.affectedProperties.forEach((property, index) => {
        this.savePriceAndAvailability(property.id, index === this.affectedProperties.length - 1);
      });
    } else {
      this.savePriceAndAvailability();
    }

    if (this.calendarStateService.featureFlagOptimistic) {
      setTimeout(() => {
        this.saving = false;
        this.clearSelected();
        this.drawer.close();
      }, 1000);
    }
  }

  savePriceAndAvailability(propertyId = this.clickedProperty.id, clear = true) {
    this.saving = true;
    const startDate = this.hoveredDates[0];
    const endDate = this.hoveredDates[this.hoveredDates.length - 1];
    const dates = eachDay(startDate, endDate);
    const property = this.properties.find((prop) => prop.id === propertyId);

    // Emit optimistic updates for each affected date
    this.calendarStateService.setOptimisticUpdates(
      propertyId,
      dates,
      this.desiredPrice,
      this.desiredAvailability,
      this.desiredLengthOfStay,
      this.desiredNote,
      this.desiredClosedForCheckin,
      this.desiredClosedForChekout,
      this.properties
    );

    this.propertiesService
      .savePricingAndAvailability({
        propertyId: Number(propertyId),
        start: format(startDate, 'YYYY-MM-DD'),
        end: format(endDate, 'YYYY-MM-DD'),
        daysOfTheWeek: this.daysOfTheWeek,
        available: this.desiredAvailability,
        price: this.desiredPrice,
        lengthOfStay: this.desiredLengthOfStay,
        maxLengthOfStay: this.desiredMaxLengthOfStay,
        note: this.desiredNote,
        closed_for_checkin: this.desiredClosedForCheckin,
        closed_for_checkout: this.desiredClosedForChekout,
      })
      .subscribe(
        async (res: any) => {
          if (res.error) {
            this.calendarStateService.handleCalendarUpdateError({
              propertyId,
              propertyName: property.name,
              dates,
              startDate,
              endDate,
            });
            this.saving = false;
          } else if (res.success) {
            const failureTypes = [
              this.desiredAvailability !== null ? this.FAILURE_TYPES.available : null,
              this.desiredPrice ? this.FAILURE_TYPES.price : null,
              this.desiredLengthOfStay !== null ? this.FAILURE_TYPES.minimum_length_of_stay : null,
            ].filter((x) => x !== null);

            if (property) {
              // Refetch the dates that have changed. We cannot calculate this locally, as we have dynamic price rules on the backend
              await this.refetchDates(property, startDate, endDate, clear, failureTypes);

              this.calendarStateService.clearOptimisticUpdates(propertyId, dates);
            }
          }
        },
        (error) => {
          this.calendarStateService.handleCalendarUpdateError({
            propertyId,
            propertyName: property.name,
            dates,
            startDate,
            endDate,
          });
          this.saving = false;
        }
      );
  }

  manualBookingAdded(event: { created?: ManualBooking; updated: ManualBooking; cancelled: ManualBooking }) {
    // if (event?.created && event?.created.propertyId) {
    //   this.clearSelected();
    //   this.drawer.close();
    //   // Bookings returned here are different to calendar events. We should refetch the events for the associated property
    //   this.fetchReservations([event.created.propertyId]);
    // }

    this.fetchUpdatedReservationDetails();
  }

  async refetchDates(property: any, startDate: Date, endDate: Date, clear = true, failureTypes?: string[]) {
    property.loading = true;

    if (!startDate || !endDate) {
      this.segmentIoService.track(SegmentEvent.CalendarDateError, {});
      return;
    }
    const res = await this.propertiesService.getPricingAndAvailability(property.id, startDate, endDate).toPromise();
    property.loading = false;
    property = Object.assign(property, res);

    let hasNewFailures = false;

    // check for failures
    if (failureTypes?.length > 0) {
      Object.values(res).forEach((day: any) => {
        if (!day.failures) {
          day.new_failures = [];
          return;
        }

        day.new_failures = day.failures.filter((failure) => {
          return failureTypes.indexOf(failure.type) > -1;
        });

        if (day.new_failures?.length > 0 && !hasNewFailures) {
          hasNewFailures = true;
        }
      });
    }
    this.updateProperties();

    if (!this.calendarStateService.featureFlagOptimistic) {
      if (clear) {
        setTimeout(() => {
          this.saving = false;

          if (!hasNewFailures) {
            this.clearSelected();
            this.drawer.close();
            this.updateProperties();
          }
        }, 2000);
      }
    }
  }

  openPropertyCalendar(propertyId: string, referrer = 'calendar') {
    this.propertyCalendarReferrerService.setReferrer(referrer);

    this.router.navigate(['/calendar/property', propertyId], { queryParams: { source: 'c' } });
  }

  openTaskSidebar() {
    this.drawer.open();
  }

  taskAdded(event: any) {
    if (!this.clickedProperty.tasks) {
      this.clickedProperty.tasks = [];
    }

    this.clickedProperty.tasks.push(event);

    this.clickedProperty.tasks = [...this.clickedProperty.tasks];
    this.updateProperties();
  }

  taskUpdated(event: any) {
    const index = this.clickedProperty.tasks.findIndex((x) => x.uuid === event.uuid);

    if (index > -1) {
      this.clickedProperty.tasks.splice(index, 1);
      this.clickedProperty.tasks.push(event);

      this.clickedProperty.tasks = [...this.clickedProperty.tasks];
    }
    this.updateProperties();
  }

  taskDeleted(uuid: any) {
    const index = this.clickedProperty.tasks.findIndex((x) => x.uuid === uuid);

    if (index > -1) {
      this.clickedProperty.tasks.splice(index, 1);
      this.clickedProperty.tasks = [...this.clickedProperty.tasks];
      this.updateProperties();
    }
  }

  // When a reservation has been modified and we need to refresh it
  fetchUpdatedReservationDetails() {
    this.clearSelected();
    this.drawer.close();
    this.fetchReservations();
  }

  openTrueParentChildReservation(childReservation) {
    this.clearSelected();
    this.drawer.close();

    // find the true reservation.
    // the uuid will be the same
    // We need to call reservationSelected(event, property) ultimately

    // we can find the property by checking childReservation.listing.property_id, thats the true source
    const truePropertyId = childReservation.listing.property_id;
    if (!truePropertyId) {
      return;
    }

    const trueProperty = this.properties.find((prop) => prop.id === truePropertyId);
    if (!trueProperty) {
      return;
    }

    let foundEvent = null;

    trueProperty.events.forEach((event: any) => {
      // todo - find the reservation with the same uuid as childReservation
      if (event.uuid === childReservation.uuid) {
        foundEvent = event;
      }
    });

    if (!foundEvent) {
      return;
    }

    this.reservationSelected({ reservation: foundEvent, property: trueProperty });
  }

  onCustomQuoteCreated(quote: DirectCustomQuote) {
    const callbackFn = (reservations) => {
      if (reservations && reservations.length > 0) {
        const property = this.properties.find((prop) => prop.id === quote.property_id);
        const reservationWithCustomQuote = reservations.find((reservation) => {
          return (
            reservation.is_custom_quote &&
            reservation.listing &&
            reservation.listing.property_id &&
            reservation.listing.property_id === quote.property_id
          );
        });

        if (reservationWithCustomQuote) {
          this.reservationSelected({ reservation: reservationWithCustomQuote, property });
        }
      }
    };

    this.clearSelected();
    this.drawer.close();
    this.fetchReservations(null, callbackFn);
  }

  public trackById = (index: number, obj): number => {
    return obj.id;
  };

  // because the parent elements can tear down and rerender the viewport, we allow the scrolling
  // event to come from the cdk DOM, instead of creating a subscription in the component
  // that would need to be managed any time the cdk component is destroyed and recreated
  public syncScroll($event) {
    this.rightHeaders.nativeElement.scrollLeft = $event.target.scrollLeft;
  }
}
